/**
 * Helper function to construct proper image URLs
 * Handles both local file paths and cloud storage URLs
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Get the full URL for an image (cover art, profile image, etc.)
 * @param {string} imagePath - The image path from the database
 * @param {string} fallback - Fallback image path (optional)
 * @returns {string} - The complete URL for the image
 */
export const getImageUrl = (imagePath, fallback = '/placeholder-cover.jpg') => {
  if (!imagePath) {
    return fallback;
  }
  
  // If the path is already a complete URL (starts with http:// or https://), return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // If it's a relative path, prepend the API URL
  return `${API_URL}${imagePath}`;
};

/**
 * Get the full URL for cover art specifically
 * @param {string} coverArtPath - The cover art path from the database
 * @returns {string} - The complete URL for the cover art
 */
export const getCoverArtUrl = (coverArtPath) => {
  return getImageUrl(coverArtPath, '/placeholder-cover.jpg');
};

/**
 * Get the full URL for profile images
 * @param {string} profileImagePath - The profile image path from the database
 * @returns {string} - The complete URL for the profile image or undefined if no image
 */
export const getProfileImageUrl = (profileImagePath) => {
  return getImageUrl(profileImagePath, undefined);
};

/**
 * Get the full URL for audio files (streaming and downloads)
 * @param {string} audioFilePath - The audio file path from the database
 * @returns {string} - The complete URL for the audio file
 */
export const getAudioFileUrl = (audioFilePath) => {
  if (!audioFilePath) {
    return null;
  }
  
  // If the path is already a complete URL (starts with http:// or https://), return as-is
  if (audioFilePath.startsWith('http://') || audioFilePath.startsWith('https://')) {
    return audioFilePath;
  }
  
  // If it's a relative path, prepend the API URL
  return `${API_URL}${audioFilePath}`;
};
